import type { ItemView } from '../types'

export function isAnswered(response: unknown): boolean {
  if (response === null || response === undefined) return false
  if (typeof response === 'object') {
    const candidate = response as Record<string, unknown>
    if (typeof candidate.option === 'string') return candidate.option.length > 0
    if (typeof candidate.value === 'boolean') return true
    if (Array.isArray(candidate.options)) return candidate.options.length > 0
    if (candidate.pairs && typeof candidate.pairs === 'object') return Object.keys(candidate.pairs).length > 0
    if (typeof candidate.text === 'string') return candidate.text.trim().length > 0
  }
  return false
}

/** The options ticked on a multiple_choice item. */
export function chosenOptions(response: unknown): string[] {
  if (!response || typeof response !== 'object') return []
  const options = (response as Record<string, unknown>).options
  return Array.isArray(options) ? options.filter((key): key is string => typeof key === 'string') : []
}

/** Ticks or unticks one option; unticking the last one clears the answer. */
export function toggledOptions(response: unknown, key: string): { options: string[] } | null {
  const current = chosenOptions(response)
  const next = current.includes(key) ? current.filter((candidate) => candidate !== key) : [...current, key]
  return next.length === 0 ? null : { options: next }
}

/** The column paired with a row on a matching item, or '' when none. */
export function pairedColumn(response: unknown, row: string): string {
  if (!response || typeof response !== 'object') return ''
  const pairs = (response as Record<string, unknown>).pairs
  if (!pairs || typeof pairs !== 'object') return ''
  const column = (pairs as Record<string, unknown>)[row]
  return typeof column === 'string' ? column : ''
}

/** Sets or clears (column '') one row; clearing the last row clears the answer. */
export function pairedResponse(response: unknown, row: string, column: string): { pairs: Record<string, string> } | null {
  const pairs: Record<string, string> = {}
  const current = response && typeof response === 'object' ? (response as Record<string, unknown>).pairs : null
  if (current && typeof current === 'object') {
    for (const [key, value] of Object.entries(current)) {
      if (typeof value === 'string') pairs[key] = value
    }
  }
  if (column === '') delete pairs[row]
  else pairs[row] = column
  return Object.keys(pairs).length === 0 ? null : { pairs }
}

/** The text typed on a short_answer or essay item. */
export function typedText(response: unknown): string {
  if (!response || typeof response !== 'object') return ''
  const text = (response as Record<string, unknown>).text
  return typeof text === 'string' ? text : ''
}

/** Empty text clears the answer so an erased field is not recorded as an answer. */
export function textResponse(text: string): { text: string } | null {
  return text.trim() === '' ? null : { text }
}

export function selectedOption(response: unknown): string | null {
  if (!response || typeof response !== 'object') return null
  const candidate = response as Record<string, unknown>
  return typeof candidate.option === 'string' ? candidate.option : null
}

export function selectedBoolean(response: unknown): boolean | null {
  if (!response || typeof response !== 'object') return null
  const candidate = response as Record<string, unknown>
  return typeof candidate.value === 'boolean' ? candidate.value : null
}

export function singleChoiceResponse(option: string): { option: string } {
  return { option }
}

export function trueFalseResponse(value: boolean): { value: boolean } {
  return { value }
}

/** Unanswered items are explicit nulls so a cleared answer is recorded too. */
export function clearedResponse(): null {
  return null
}

const typeLabels: Record<ItemView['type'], string> = {
  single_choice: 'Pilihan tunggal',
  true_false: 'Benar / Salah',
  multiple_choice: 'Pilihan ganda kompleks',
  matching: 'Menjodohkan',
  short_answer: 'Isian singkat',
  essay: 'Uraian',
}

export function itemTypeLabel(item: ItemView): string {
  return typeLabels[item.type] ?? item.type
}
